/**
 * Settings pages cut from twin-ci System Settings (mockup system-settings sections moved to their own
 * pages). `group` is the api/system-settings/{group} whitelist in config/system_settings.php (the
 * server validates again); `field` is the `setting` column; option values are twin-ci's.
 */
export interface SystemField {
    field: string;
    label: string;
    type: 'int' | 'decimal' | 'text' | 'email' | 'phone' | 'radio' | 'select' | 'checkbox' | 'html' | 'image' | 'link' | 'heading';
    options?: { value: any, label: string }[];
    required?: boolean;
    full?: boolean;     // spans both columns
    hint?: string;      // 'heading': the line under it
    min?: number;       // numbers: lowest value (default 0)
    unit?: '$' | '%';   // shown in the label, e.g. "Office Markup (%)"
    ciSlug?: string;    // 'link': a twin-ci page (through Laravel's url/<slug> redirector)
    route?: string;     // 'link': a page of this app
    linkText?: string;
    optionsUrl?: string; // 'select': load options ({id, name}[]) from this app asset
    maxLength?: number;
    accept?: string;    // 'image': file types offered (the server checks again)
    preview?: 'dark' | 'light';   // 'image': mockup .system-branding-preview box instead of "Current ..."
    currentLabel?: string;        // 'image': label of the current file (default "Current <label>")
    selectLabel?: string;         // 'image': label of the file picker (default "Select [Replacement] <label> File")
    mockOnly?: boolean; // shown as in the mockup but disabled and never saved (no setting behind it yet)
    short?: boolean;    // 'select': narrow (the mockup's State / time controls); selects are wide otherwise
    checkedValue?: string; // 'checkbox': the value saved when ticked ('' when not) instead of 1 / 0
    default?: any;      // shown (and saved on the next Save) while nothing is saved yet
}

/** One section (card) with its own Save; `group` is its api/system-settings group. */
export interface SystemCard {
    group: string;
    title: string;
    fields: SystemField[];
    help?: string;
}

/** A page is one section, or several (each saved on its own, the first open). */
export type SystemFormPage = SystemCard | { cards: SystemCard[] };

const radio = (field: string, label: string, options: { value: any, label: string }[]): SystemField =>
    ({ field, label, type: 'radio', options, required: true, full: true });
const int = (field: string, label: string, min = 0): SystemField => ({ field, label, type: 'int', required: true, min });
const text = (field: string, label: string, maxLength: number, type: 'text' | 'email' | 'phone' = 'text'): SystemField =>
    ({ field, label, type, maxLength });
const heading = (label: string, hint?: string): SystemField => ({ field: '', label, type: 'heading', full: true, hint });
const decimal = (field: string, label: string, unit?: '$' | '%'): SystemField => ({ field, label, type: 'decimal', required: true, unit });

export const SYSTEM_FORM_PAGES: { [key: string]: SystemFormPage } = {
    'amount-rounding': {
        group: 'amount-rounding', title: 'Whole Dollar Rounding',
        fields: [radio('disable_round', 'Dollar Amounts', [{ value: 1, label: 'Use Whole Dollars' }, { value: 0, label: 'Allow partial dollars (cents)' }])]
    },
    'fee-charge-date': {
        group: 'fee-charge-date', title: 'Inspection Fee Charge Date',
        fields: [radio('charge_post_date', 'Charge Date', [
            { value: 2, label: 'Scheduled Date' }, { value: 1, label: 'Inspection Date' }, { value: 3, label: 'Inspection Complete Date' }])]
    },
    // Read by the hourly weekly_report:generate command (day name + hour; minutes are ignored).
    'weekly-report': {
        group: 'weekly-report', title: 'Inspector Weekly Job Report',
        fields: [
            { field: 'report_mail_send_day', label: 'Send Mail Day of Week', type: 'select', required: true, default: 'monday',
              options: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
                  .map(d => ({ value: d, label: d.charAt(0).toUpperCase() + d.slice(1) })) },
            { field: 'report_mail_send_time', label: 'Send Mail Time', type: 'select', required: true, short: true, default: '2:00',
              options: Array.from(Array(24).keys()).map(h => ({ value: h + ':00',
                  label: ((h % 12) || 12) + ':00 ' + (h < 12 ? 'AM' : 'PM') })) },
            // twin-ci: at least one duration must be ticked; "last week" is the only one offered.
            { field: 'report_mail_send_duration', label: 'Duration — Last Week', type: 'checkbox', checkedValue: 'last week',
              required: true, full: true, default: 'last week' },
            Object.assign(decimal('report_mail_criteria_usd_amount', 'Criteria of', '$'), { min: 0.01, default: 1 }),
        ]
    },
    'tab-order': {
        group: 'tab-order', title: 'Inspection Tab Order',
        fields: [radio('inspection_tab_order', 'Default Tab Order', [
            { value: 0, label: 'Client, Property, Scheduling' }, { value: 1, label: 'Property, Client, Scheduling' }])]
    },
    'report-workflow': {
        group: 'report-workflow', title: 'Report Workflow Defaults',
        fields: [radio('setting_separated_reports_default', 'Default Workflow', [
            { value: 1, label: 'Real Estate / Escrow / Separated' }, { value: 0, label: 'Non-real estate / Consumer' }])],
        help: 'Real Estate: the Separated Report disclosure is added to the report before the Findings, items have ' +
            'Section 1 and 2 headings, and the Work Contract breaks out the Section 1 and 2 items with totals. ' +
            'Non-real estate: no Section 1 and 2 headings on the report or the Work Contract, and Intake > Scheduling ' +
            'shows a "Real-estate transaction / Escrow" checkbox that applies the Real Estate / Escrow rules to that inspection only.'
    },
    'time-calculator': {
        group: 'time-calculator', title: 'Inspection Time Calculator',
        fields: [
            heading('Base Values'),
            int('setting_inspection_minutes', 'Inspection Minutes'),
            int('setting_subarea_additional_minutes', 'Subarea Additional Minutes'),
            int('setting_square_feet', 'Square Feet'),
            int('setting_crawl_square_feet', 'Crawl Square Feet'),
            int('setting_home_age', 'Home Age (Years)'),
            heading('Additional Minutes'),
            decimal('setting_minute_square_feet_base', 'Minutes per Sq Ft above Base'),
            decimal('setting_minute_square_feet_subarea', 'Minutes per Sq Ft for Subarea'),
            decimal('setting_multi_story_multiplier', 'Multi-Story Crawl Multiplier'),
            decimal('setting_minute_year_older_base', 'Minutes per Year older than Base'),
        ]
    },
    'pricing': {
        group: 'pricing', title: 'Bid Pricing & Markups',
        fields: [
            // twin-ci shows the same link: the crew rate is set per branch (mockup: Territories & Branches).
            { field: 'crew-rate', label: 'Crew Hourly Rate (master)', type: 'link', full: true, route: '/branches', linkText: 'Edit rates in Territories & Branches' },
            decimal('setting_bid_rate_materials', 'Materials Markup', '%'),
            decimal('setting_bid_rate_subcontractor', 'Subcontractor Markup', '%'),
            decimal('setting_bid_rate_office', 'Office Markup', '%'),
            { field: 'standard_lead_crew_rate', label: 'Standard Lead Crew Rate', type: 'int', required: true, unit: '$' },
            decimal('treat_standard_rate', 'Treat Standard Rate – Gas', '$'),
            decimal('treat_standard_rate_liquid', 'Treat Standard Rate – Liquid', '$'),
            radio('chemical_bid_priority', 'Chemical Bid Priority', [{ value: 'fume', label: 'Fume' }, { value: 'liquid', label: 'Liquid / Foam' }]),
            decimal('crew_sub_overhead', 'Crew and Subcontractor Overhead', '%'),
            decimal('office_support_overhead', 'In-House Office & Support Overhead', '%'),
            decimal('sub_overhead', 'Subcontracted Overhead', '%'),
            decimal('sub_office_support_overhead', 'Subcontracted Office & Support Overhead', '%'),
        ]
    },
    // System Settings is now only the mockup's "General" section; the rest moved to its own pages.
    'system': {
        group: 'general', title: 'General',
        fields: [
            Object.assign(text('setting_website_name', 'Application Name', 255), { required: true }),
            Object.assign(text('setting_url', 'Application URL', 255), { required: true }),
            text('setting_email_support', 'Support Email Address', 255, 'email'),
            text('setting_phone_support', 'Support Phone Number', 25, 'phone'),
            Object.assign(text('setting_email_admin', 'Admin Email Address', 255, 'email'), { required: true }),
            Object.assign(text('setting_email_test', 'Test Email Address', 255, 'email'), { required: true }),
            // In the mockup, but no such settings exist yet - waiting on the client (2026-10-09).
            { field: 'time_zone', label: 'Time Zone', type: 'select', mockOnly: true,
              options: [{ value: 'Pacific', label: 'Pacific' }, { value: 'Mountain', label: 'Mountain' }, { value: 'Central', label: 'Central' }, { value: 'Eastern', label: 'Eastern' }] },
            { field: 'date_format', label: 'Date Format', type: 'select', mockOnly: true,
              options: [{ value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' }, { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' }] },
            { field: 'first_day_of_week', label: 'First Day of Week', type: 'select', mockOnly: true,
              options: [{ value: 'Sunday', label: 'Sunday' }, { value: 'Monday', label: 'Monday' }] },
        ]
    },
    'company-profile': {
        group: 'company-profile', title: 'Company Details',
        fields: [
            text('setting_company_name', 'Company Name', 255),
            text('setting_company_registration', 'Company Registration #', 255),
            text('setting_company_address1', 'Street Address', 255),
            text('setting_company_address2', 'Street Address 2', 255),
            text('setting_company_city', 'City', 255),
            { field: 'setting_company_state', label: 'State', type: 'select', required: true, short: true, optionsUrl: 'assets/states.json' },
            text('setting_company_zip', 'ZIP', 25),
            text('setting_company_phone1', 'Phone', 25, 'phone'),
            text('setting_company_phone2', 'Alt. Phone', 25, 'phone'),
            text('setting_company_fax', 'Fax', 25, 'phone'),
            text('setting_company_website_url', 'Company Website URL', 255),
            text('setting_company_email', 'Company Email', 255, 'email'),
        ]
    },
    'dashboard-settings': {
        cards: [
            {
                group: 'dashboard-messages', title: 'Dashboard Messages',
                fields: [
                    { field: 'setting_cms_dashboard_admin', label: 'Administrator’s Dashboard Text', type: 'html', full: true },
                    { field: 'setting_cms_dashboard_agent', label: 'Field Agent Dashboard Text', type: 'html', full: true },
                ]
            },
            {
                group: 'dashboard-preferences', title: 'Dashboard Preferences',
                fields: [
                    { field: 'setting_view_widget_inspections_scheduled', label: 'Show on Dashboard - Inspections Scheduled', type: 'checkbox', full: true },
                ]
            },
        ]
    },
    'official-signature': {
        group: 'official-signature', title: 'Official Signature',
        fields: [
            { field: 'setting_signature_main_printed', label: 'Printed Name', type: 'text', required: true, maxLength: 100 },
            { field: 'setting_signature_main_file', label: 'Signature Image', type: 'image', required: true, full: true,
              currentLabel: 'Current Signature', selectLabel: 'Select Replacement Signature Image File',
              accept: 'image/png,image/jpeg,image/gif', hint: 'Recommended size: 260 × 60px' },
        ]
    },
    // Section names and order as the mockup shows them (alphabetical): Favicon, Report & Job Card
    // Logos, Sidebar & Login Logos. PDF logo sizes are exact, as twin-ci checks them (the server
    // checks again); their notes are unchanged (agreed 2026-10-21).
    'branding': {
        cards: [
            {
                group: 'branding-favicon', title: 'Favicon',
                fields: [
                    { field: 'settings_favicon_image', label: 'Favicon Image', type: 'image', full: true, preview: 'light', selectLabel: 'Select Favicon Image',
                      accept: 'image/png,image/jpeg,image/x-icon,image/vnd.microsoft.icon',
                      hint: 'Recommended size: 16 × 16px. jpg, png or ico, at most 173 × 173px and 25 KB.' },
                ]
            },
            {
                group: 'branding-report-logos', title: 'Report & Job Card Logos',
                fields: [
                    { field: 'settings_logo_report_pdf', label: 'Logo for Report PDF', type: 'image', full: true, preview: 'light', selectLabel: 'Select Logo for Report PDF',
                      accept: 'image/png,image/jpeg,image/gif', hint: 'Exactly 173 × 173px; jpg, png or gif under 4 MB.' },
                    { field: 'settings_logo_jc_pdf', label: 'Logo for Job Card PDF', type: 'image', full: true, preview: 'light', selectLabel: 'Select Logo for Job Card PDF',
                      accept: 'image/png,image/jpeg,image/gif', hint: 'Exactly 300 × 116px; jpg, png or gif under 4 MB.' },
                ]
            },
            {
                // Sidebar first, on the dark sidebar colour; login on white (mockup previews).
                group: 'branding-logos', title: 'Sidebar & Login Logos',
                fields: [
                    { field: 'settings_logo_sidebar_image', label: 'Logo Sidebar Image', type: 'image', full: true, preview: 'dark',
                      accept: 'image/png,image/jpeg,image/gif,image/svg+xml', hint: 'Full logo: 390 x 96px. Compact mark: 92 x 96px (the left part of the logo, shown when the menu is collapsed). svg, jpg, png or gif under 1 MB; SVG scales to fit.' },
                    { field: 'settings_logo_login_image', label: 'Logo Login Image', type: 'image', full: true, preview: 'light',
                      accept: 'image/png,image/jpeg,image/gif,image/svg+xml', hint: 'Recommended aspect ratio: 390:96. SVG scales to fit; jpg, png or gif 150 to 1200px wide and 40 to 400px tall. Under 1 MB.' },
                ]
            },
        ]
    },
    'agent-outreach': {
        group: 'agent-outreach', title: 'Agent Outreach',
        fields: [
            heading('New Agents', 'Triggers when agent has exactly 1 inspection placed within this window.'),
            int('outreach_new_agent_days', 'Placed first Inspection in last (Days)', 1),
            heading('VIP Status Achieved'),
            int('outreach_vip_inspections', 'Inspections'),
            { field: 'outreach_vip_condition', label: 'Condition', type: 'select', required: true,
              options: [{ value: 'and', label: 'AND' }, { value: 'or', label: 'OR' }] },
            int('outreach_vip_completions', 'Completions'),
            heading('Agent Dropoff'),
            int('outreach_dropoff_inactive_months', 'Has not ordered in (Months)', 1),
            int('outreach_dropoff_min_orders', 'But ordered at least (Inspections)', 1),
            int('outreach_dropoff_order_months', 'In last (Months)', 1),
        ]
    },
};

// Pages the mockup shows as one page with sections (agreed 2026-10-21). Sections in the mockup's
// (alphabetical) order; each section still saves on its own. The old single-section URLs redirect
// to these (app-routing).
const section = (key: string): SystemCard => SYSTEM_FORM_PAGES[key] as SystemCard;
const branding = (SYSTEM_FORM_PAGES['branding'] as { cards: SystemCard[] }).cards;
// General Settings
SYSTEM_FORM_PAGES['inspection-general'] = {
    cards: [section('fee-charge-date'), section('tab-order'), section('report-workflow'), section('amount-rounding')]
};
// Company Profile & Branding: Company Details, Favicon, Official Signature, Report & Job Card
// Logos, Sidebar & Login Logos.
SYSTEM_FORM_PAGES['company-profile'] = {
    cards: [section('company-profile'), branding[0], section('official-signature'), branding[1], branding[2]]
};
